import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Mail,
  Search,
  Eye,
  X,
  Phone,
  User,
  MessageSquare,
  Calendar,
  Trash2,
  ChevronDown,
  Check
} from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import StatCards from "../components/StatCards";
import UpdateSuccessModal from "../components/UpdateSuccessModal";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import DeleteSuccessModal from "../components/DeleteSuccessModal";
import { getAllContacts, updateContactStatus, deleteContact } from "../services/api";
import "./Dashboard.css";
import "./Contact.css";

const DEFAULT_ITEMS_PER_PAGE = 10;
const PER_PAGE_OPTIONS = [5, 10, 20, 50];

// Status mapping: API lowercase -> Display format
const STATUS_MAP = {
  new: "New",
  contacted: "Contacted",
  resolved: "Resolved",
  closed: "Closed",
};

const STATUS_FILTER_OPTIONS = [
  { value: "All", label: "All Statuses" },
  { value: "Contacted", label: "Contacted" },
  { value: "Resolved", label: "Resolved" },
  { value: "New", label: "New" },
];

const MODAL_STATUS_OPTIONS = [
  { value: "New", label: "New" },
  { value: "Contacted", label: "Contacted" },
  { value: "Resolved", label: "Resolved" },
];

const REVERSE_STATUS_MAP = {
  New: "new",
  Contacted: "contacted",
  Resolved: "resolved",
  Closed: "closed",
};

// Format date to readable format: "12 Sept 2026"
const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  } catch (e) {
    return dateStr;
  }
};

const toDateStr = (d) => {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const fmtDisp = (s) => {
  if (!s) return "";
  const d = new Date(s + "T12:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// Custom Select Component matching premium admin theme
function CustomSelect({ value, onChange, options, disabled, placeholder = "Select" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (ev) => {
      if (ref.current && !ref.current.contains(ev.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value) || {
    value,
    label: value || placeholder,
  };

  return (
    <div
      ref={ref}
      className={"ce-custom-select" + (open ? " is-open" : "") + (disabled ? " is-disabled" : "")}
    >
      <button
        type="button"
        className="ce-custom-select-trigger"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="ce-custom-select-label">{selectedOption.label}</span>
        <ChevronDown className={"ce-custom-select-arrow" + (open ? " rotated" : "")} size={14} />
      </button>

      {open && (
        <div className="ce-custom-select-dropdown" role="listbox">
          {options.map((opt) => {
            const isSelected = opt.value === value;
            return (
              <div
                key={opt.value}
                className={"ce-custom-select-item" + (isSelected ? " is-selected" : "")}
                onClick={() => {
                  onChange(opt.value);
                  setOpen(false);
                }}
                role="option"
                aria-selected={isSelected}
              >
                <span>{opt.label}</span>
                {isSelected && <Check className="ce-custom-select-check" size={14} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Toast notification component
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return <div className={`ce-toast ce-toast--${type}`}>{message}</div>;
}

function StatusBadge({ status }) {
  const raw = (status || "").toLowerCase();
  const displayStatus = STATUS_MAP[raw] || status || "New";
  const statusClassMap = {
    new: "ce-badge ce-badge--new",
    contacted: "ce-badge ce-badge--contacted",
    resolved: "ce-badge ce-badge--resolved",
    closed: "ce-badge ce-badge--closed",
  };
  return <span className={statusClassMap[raw] || "ce-badge"}>{displayStatus}</span>;
}

function DetailRow({ icon, label, value }) {
  return (
    <div className="ce-modal-row">
      <div className="ce-modal-row-icon">{icon}</div>
      <div className="ce-modal-row-content">
        <span className="ce-modal-row-label">{label}</span>
        <span className="ce-modal-row-value">{value}</span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   DASHBOARD-MATCHING DATE FILTER POPUP & CALENDARS
   ───────────────────────────────────────────────────────────── */
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const buildCalendarCells = (year, month) => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i += 1) cells.push(null);
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(new Date(year, month, d));
  while (cells.length % 7 !== 0) cells.push(null);
  while (cells.length < 42) cells.push(null);
  return cells;
};

const MiniCalendar = ({
  year,
  month,
  start,
  end,
  hoverDate,
  onDayClick,
  onDayHover,
  onPrev,
  onNext,
  showPrev = true,
  showNext = true,
}) => {
  const todayStr = toDateStr(new Date());
  const cells = buildCalendarCells(year, month);

  const isToday = (d) => d && toDateStr(d) === todayStr;
  const isInRange = (d) => {
    if (!d) return false;
    const s = toDateStr(d);
    const rangeEnd = end || hoverDate || "";
    if (!start || !rangeEnd) return false;
    const lo = start < rangeEnd ? start : rangeEnd;
    const hi = start < rangeEnd ? rangeEnd : start;
    return s > lo && s < hi;
  };
  const isRangeStart = (d) => {
    if (!d) return false;
    const s = toDateStr(d);
    const rangeEnd = end || hoverDate || "";
    if (!start || !rangeEnd) return s === start;
    return s === (start < rangeEnd ? start : rangeEnd);
  };
  const isRangeEnd = (d) => {
    if (!d) return false;
    const s = toDateStr(d);
    const rangeEnd = end || hoverDate || "";
    if (!start || !rangeEnd) return false;
    return s === (start < rangeEnd ? rangeEnd : start);
  };

  return (
    <div className="dc-cal">
      <div className="dc-cal-nav">
        {showPrev ? (
          <button type="button" className="dc-cal-nav-btn" onClick={onPrev}>‹</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
        <span className="dc-cal-month-label">{MONTHS[month]} {year}</span>
        {showNext ? (
          <button type="button" className="dc-cal-nav-btn" onClick={onNext}>›</button>
        ) : (
          <div style={{ width: 28 }} />
        )}
      </div>
      <div className="dc-cal-weekdays">
        {WEEKDAYS.map((wd) => (
          <div key={wd} className="dc-cal-weekday">{wd}</div>
        ))}
      </div>
      <div className="dc-cal-days">
        {cells.map((cell, idx) => {
          if (!cell) return <div key={"b-" + idx} className="dc-cal-day is-empty" />;
          let cls = "dc-cal-day";
          if (isToday(cell)) cls += " is-today";
          if (isRangeStart(cell)) cls += " is-sel-start";
          if (isRangeEnd(cell)) cls += " is-sel-end";
          if (isInRange(cell)) cls += " is-in-range";
          return (
            <button
              key={toDateStr(cell)}
              type="button"
              className={cls}
              onClick={() => onDayClick && onDayClick(cell)}
              onMouseEnter={() => onDayHover && onDayHover(toDateStr(cell))}
            >
              {cell.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const DateFilterPopup = ({
  initialStart = "",
  initialEnd = "",
  initialPreset = "7d",
  onApply,
  onClose,
  hasRecords = true,
}) => {
  const todayDate = new Date();
  const todayStr = toDateStr(todayDate);
  const [start, setStart] = useState(initialStart || todayStr);
  const [end, setEnd] = useState(initialEnd || todayStr);
  const [preset, setPreset] = useState(initialPreset || "today");
  const [pickStep, setPickStep] = useState("start");
  const [hoverDate, setHoverDate] = useState("");
  const [leftYear, setLeftYear] = useState(todayDate.getFullYear());
  const [leftMonth, setLeftMonth] = useState(todayDate.getMonth() === 0 ? 11 : todayDate.getMonth() - 1);
  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  const goLeft = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear((y) => y - 1);
    } else {
      setLeftMonth((m) => m - 1);
    }
  };

  const goRight = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear((y) => y + 1);
    } else {
      setLeftMonth((m) => m + 1);
    }
  };

  const applyPreset = (key) => {
    setPreset(key);
    const now = new Date();
    let s = "";
    let e2 = "";
    if (key === "today") {
      s = toDateStr(now);
      e2 = toDateStr(now);
    } else if (key === "yesterday") {
      const y = new Date(now);
      y.setDate(now.getDate() - 1);
      s = toDateStr(y);
      e2 = toDateStr(y);
    } else if (key === "7d") {
      const d7 = new Date(now);
      d7.setDate(now.getDate() - 6);
      s = toDateStr(d7);
      e2 = toDateStr(now);
    } else if (key === "30d") {
      const d30 = new Date(now);
      d30.setDate(now.getDate() - 29);
      s = toDateStr(d30);
      e2 = toDateStr(now);
    } else if (key === "this_month") {
      const tm = new Date(now.getFullYear(), now.getMonth(), 1);
      s = toDateStr(tm);
      e2 = toDateStr(now);
    } else if (key === "custom") {
      setStart("");
      setEnd("");
      setPickStep("start");
      return;
    }
    setStart(s);
    setEnd(e2);
    setPickStep("start");
    setHoverDate("");
  };

  const handleDayClick = (dateObj) => {
    if (!dateObj) return;
    const str = toDateStr(dateObj);
    setPreset("custom");
    if (pickStep === "start" || (start && end)) {
      setStart(str);
      setEnd("");
      setPickStep("end");
    } else {
      if (str < start) {
        setStart(str);
        setEnd(start);
      } else {
        setEnd(str);
      }
      setPickStep("start");
      setHoverDate("");
    }
  };

  const handleApply = () => {
    let applyStart = start;
    let applyEnd = end;
    if (start && !end) applyEnd = start;
    onApply(applyStart, applyEnd, preset);
  };

  const handleClear = () => {
    setStart("");
    setEnd("");
    setPreset("7d");
    setPickStep("start");
    setHoverDate("");
    onApply("", "", "all");
  };

  const presets = [
    { key: "today", label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "7d", label: "Last 7 Days" },
    { key: "30d", label: "Last 30 Days" },
    { key: "this_month", label: "This Month" },
    { key: "custom", label: "Custom" },
  ];

  const popupElement = (
    <div className="dfd-overlay" onClick={onClose}>
      <div className="dfd-popup" onClick={(ev) => ev.stopPropagation()}>
        <div className="dfd-header">
          <div>
            <div className="dfd-header-title">Filter by Date</div>
            <div className="dfd-header-sub">Select a date range to filter contact enquiries</div>
          </div>
          <button type="button" className="dfd-close-btn" onClick={onClose}>×</button>
        </div>

        <div className="dfd-body">
          <div className="dfd-calendars">
            <div className="dfd-range-display">
              <div className={"dfd-range-field" + (pickStep === "start" ? " is-picking" : "")}>
                <div className="dfd-range-field-label">Start Date</div>
                <div className="dfd-range-field-value">{start ? fmtDisp(start) : "mm/dd/yyyy"}</div>
              </div>
              <div className="dfd-range-sep">
                <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
              <div className={"dfd-range-field" + (pickStep === "end" ? " is-picking" : "")}>
                <div className="dfd-range-field-label">End Date</div>
                <div className="dfd-range-field-value">{end ? fmtDisp(end) : "mm/dd/yyyy"}</div>
              </div>
            </div>

            <div className="dfd-dual-cal" onMouseLeave={() => setHoverDate("")}>
              <MiniCalendar
                year={leftYear}
                month={leftMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={(s) => { if (pickStep === "end" && start) setHoverDate(s); }}
                onPrev={goLeft}
                onNext={null}
                showPrev={true}
                showNext={false}
              />
              <div className="dfd-cal-divider" />
              <MiniCalendar
                year={rightYear}
                month={rightMonth}
                start={start}
                end={end}
                hoverDate={hoverDate}
                onDayClick={handleDayClick}
                onDayHover={(s) => { if (pickStep === "end" && start) setHoverDate(s); }}
                onPrev={null}
                onNext={goRight}
                showPrev={false}
                showNext={true}
              />
            </div>

            {(start || end) && !hasRecords && (
              <div className="dfd-no-data-notice">
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <circle cx={12} cy={12} r={10} />
                  <line x1={12} y1={8} x2={12} y2={12} />
                  <line x1={12} y1={16} x2={12.01} y2={16} />
                </svg>
                No contact enquiries submitted in this date range
              </div>
            )}
          </div>

          <div className="dfd-presets">
            <div className="dfd-presets-title">Quick Select</div>
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                className={"dfd-preset-btn" + (preset === p.key ? " is-active" : "")}
                onClick={() => applyPreset(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        <div className="dfd-footer">
          <div style={{ flex: 1 }} />
          <div className="dfd-action-btns">
            <button type="button" className="dfd-clear-btn" onClick={handleClear}>Clear</button>
            <button
              type="button"
              className="dfd-apply-btn"
              onClick={handleApply}
              disabled={!start && !end}
            >
              <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(popupElement, document.body)
    : popupElement;
};

/* ─────────────────────────────────────────────────────────────
   VIEW ENQUIRY DETAILS MODAL
   ───────────────────────────────────────────────────────────── */
function EnquiryDetailsModal({ enquiry, onClose, onStatusUpdate, isUpdating, onDelete }) {
  const [selectedStatus, setSelectedStatus] = useState("");

  useEffect(() => {
    if (enquiry) {
      setSelectedStatus(STATUS_MAP[enquiry.status?.toLowerCase()] || enquiry.status || "New");
    }
  }, [enquiry]);

  if (!enquiry) return null;

  const currentStatusDisplay = STATUS_MAP[enquiry.status?.toLowerCase()] || enquiry.status || "New";

  const handleStatusChange = async () => {
    const apiStatus = REVERSE_STATUS_MAP[selectedStatus];
    if (apiStatus && apiStatus !== (enquiry.status || "").toLowerCase()) {
      await onStatusUpdate(enquiry._id, apiStatus);
    }
  };

  const modalElement = (
    <div className="ce-modal-overlay" onClick={onClose}>
      <div className="ce-modal-content" onClick={(ev) => ev.stopPropagation()}>
        <div className="ce-modal-header">
          <h2 className="ce-modal-title">Enquiry Details</h2>
          <button
            className="ce-modal-close"
            onClick={onClose}
            type="button"
            aria-label="Close modal"
          >
            <X size={18} />
          </button>
        </div>

        <div className="ce-modal-body">
          <div className="ce-modal-section">
            <DetailRow icon={<User size={15} />} label="Name" value={enquiry.name || "-"} />
            <DetailRow icon={<Phone size={15} />} label="Phone" value={enquiry.phone || "-"} />
            <DetailRow icon={<Mail size={15} />} label="Email" value={enquiry.email || "-"} />
            <DetailRow icon={<MessageSquare size={15} />} label="Subject" value={enquiry.subject || "-"} />
            <DetailRow icon={<Calendar size={15} />} label="Submitted Date" value={formatDate(enquiry.createdAt)} />
            <div className="ce-modal-row">
              <div className="ce-modal-row-icon"><Eye size={15} /></div>
              <div className="ce-modal-row-content">
                <span className="ce-modal-row-label">Current Status</span>
                <div><StatusBadge status={enquiry.status} /></div>
              </div>
            </div>
          </div>

          <div className="ce-modal-section">
            <h3 className="ce-modal-section-title">Message</h3>
            <div className="ce-modal-message">{enquiry.message || "-"}</div>
          </div>

          <div className="ce-modal-section">
            <h3 className="ce-modal-section-title">Update Status</h3>
            <div className="ce-status-update-group">
              <CustomSelect
                value={selectedStatus}
                onChange={(newVal) => setSelectedStatus(newVal)}
                disabled={isUpdating}
                options={MODAL_STATUS_OPTIONS}
              />
              <button
                className="ce-modal-btn ce-modal-btn--update"
                onClick={handleStatusChange}
                disabled={isUpdating || selectedStatus === currentStatusDisplay}
                type="button"
              >
                {isUpdating ? "Updating..." : "Update Status"}
              </button>
            </div>
          </div>
        </div>

        <div className="ce-modal-footer">
          <button
            type="button"
            className="ce-modal-btn ce-modal-btn--delete"
            onClick={() => onDelete && onDelete(enquiry)}
          >
            <Trash2 size={14} />
            Delete Enquiry
          </button>
          <button
            type="button"
            className="ce-modal-btn ce-modal-btn--close"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(modalElement, document.body)
    : modalElement;
}

/* ─────────────────────────────────────────────────────────────
   MAIN COMPONENT: ContactEnquiries
   ───────────────────────────────────────────────────────────── */
export default function ContactEnquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [filterPreset, setFilterPreset] = useState("all");
  const [showDateFilter, setShowDateFilter] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);

  // Modals & Action States
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(false);

  // Fetch enquiries from backend
  const fetchEnquiries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getAllContacts();
      const data = response?.data?.data || [];
      setEnquiries(data);
    } catch (err) {
      console.error("Error fetching enquiries:", err);
      setError(err?.response?.data?.message || "Failed to fetch enquiries");
      setToast({
        message: "Failed to load enquiries. Please try again.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  // 1. Date filter (filters enquiries by submitted date createdAt)
  const dateFilteredEnquiries = useMemo(() => {
    if (!filterStart && !filterEnd) return enquiries;
    const startDate = filterStart ? new Date(filterStart + "T00:00:00") : null;
    const endDate = filterEnd ? new Date(filterEnd + "T23:59:59.999") : null;

    return enquiries.filter((item) => {
      if (!item.createdAt) return false;
      const d = new Date(item.createdAt);
      if (Number.isNaN(d.getTime())) return false;
      if (startDate && d < startDate) return false;
      if (endDate && d > endDate) return false;
      return true;
    });
  }, [enquiries, filterStart, filterEnd]);

  // 2. Stack Card counts (reflects date-filtered range if set, or all enquiries)
  const totalCount = dateFilteredEnquiries.length;
  const contactedCount = dateFilteredEnquiries.filter(
    (item) => (item.status || "").toLowerCase() === "contacted"
  ).length;
  const resolvedCount = dateFilteredEnquiries.filter(
    (item) => ["resolved", "closed"].includes((item.status || "").toLowerCase())
  ).length;
  const newCount = dateFilteredEnquiries.filter(
    (item) => ["new", ""].includes((item.status || "").toLowerCase()) || !item.status
  ).length;

  const statCards = [
    {
      title: "Contacted",
      value: contactedCount,
      icon: "message",
      description: "Enquiries reached out",
      accent: "#10b981",
      active: statusFilter === "Contacted",
      onClick: () => {
        setStatusFilter(statusFilter === "Contacted" ? "All" : "Contacted");
        setCurrentPage(1);
      },
    },
    {
      title: "Resolved",
      value: resolvedCount,
      icon: "check",
      description: "Issues closed & solved",
      accent: "#8b5cf6",
      active: statusFilter === "Resolved",
      onClick: () => {
        setStatusFilter(statusFilter === "Resolved" ? "All" : "Resolved");
        setCurrentPage(1);
      },
    },
    {
      title: "New",
      value: newCount,
      icon: "clock",
      description: "Fresh incoming messages",
      accent: "#0ea5e9",
      active: statusFilter === "New",
      onClick: () => {
        setStatusFilter(statusFilter === "New" ? "All" : "New");
        setCurrentPage(1);
      },
    },
    {
      title: "All Status",
      value: totalCount,
      icon: "grid",
      description: "Total contact messages",
      accent: "#f59e0b",
      active: statusFilter === "All",
      onClick: () => {
        setStatusFilter("All");
        setCurrentPage(1);
      },
    },
  ];

  // 3. Search & Status filter
  const filteredEnquiries = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return dateFilteredEnquiries.filter((item) => {
      const displayStatus = STATUS_MAP[item.status?.toLowerCase()] || item.status || "New";
      const matchesStatus =
        statusFilter === "All" ||
        displayStatus.toLowerCase() === statusFilter.toLowerCase();

      const matchesSearch =
        !term ||
        (item.name || "").toLowerCase().includes(term) ||
        (item.email || "").toLowerCase().includes(term) ||
        (item.subject || "").toLowerCase().includes(term) ||
        (item.phone || "").toLowerCase().includes(term);

      return matchesStatus && matchesSearch;
    });
  }, [dateFilteredEnquiries, searchTerm, statusFilter]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, filterStart, filterEnd]);

  // 4. Pagination calculations
  const totalPages = Math.max(1, Math.ceil(filteredEnquiries.length / itemsPerPage));
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (safeCurrentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredEnquiries.length);
  const pageItems = filteredEnquiries.slice(startIndex, endIndex);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, safeCurrentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  // Date filter handlers
  const handleDateApply = (start, end, preset) => {
    setFilterStart(start);
    setFilterEnd(end);
    setFilterPreset(preset);
    setShowDateFilter(false);
  };

  const handleClearDate = () => {
    setFilterStart("");
    setFilterEnd("");
    setFilterPreset("all");
    setShowDateFilter(false);
  };

  const dateFilterButtonLabel = useMemo(() => {
    if (!filterStart && !filterEnd) return "Filter by Date";
    if (filterStart && filterEnd && filterStart === filterEnd) {
      return fmtDisp(filterStart);
    }
    if (filterStart && filterEnd) {
      return `${fmtDisp(filterStart)} – ${fmtDisp(filterEnd)}`;
    }
    if (filterStart) return `From ${fmtDisp(filterStart)}`;
    return `Until ${fmtDisp(filterEnd)}`;
  }, [filterStart, filterEnd]);

  // Handle status update
  const handleStatusUpdate = useCallback(
    async (enquiryId, newStatus) => {
      try {
        setUpdatingId(enquiryId);
        const response = await updateContactStatus(enquiryId, { status: newStatus });

        if (response?.data?.data) {
          // Update in local state immediately so Stack Cards and table refresh
          setEnquiries((prev) =>
            prev.map((item) =>
              item._id === enquiryId ? response.data.data : item
            )
          );

          // Update currently viewed item if open
          if (selectedEnquiry && selectedEnquiry._id === enquiryId) {
            setSelectedEnquiry(response.data.data);
          }

          setShowUpdateSuccess(true);
        }
      } catch (err) {
        console.error("Error updating status:", err);
        setToast({
          message: err?.response?.data?.message || "Failed to update status. Please try again.",
          type: "error",
        });
      } finally {
        setUpdatingId(null);
      }
    },
    [selectedEnquiry]
  );

  // Handle delete enquiry
  const handleDeleteEnquiry = async (enquiryId) => {
    try {
      setIsDeleting(true);
      await deleteContact(enquiryId);

      setDeleteTarget(null);
      setDeleteSuccess({ id: enquiryId });
    } catch (err) {
      console.error("Error deleting enquiry:", err);
      setToast({
        message: err?.response?.data?.message || "Failed to delete enquiry. Please try again.",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSuccessClose = () => {
    const deletedId = deleteSuccess?.id;
    setDeleteSuccess(null);
    if (deletedId) {
      setEnquiries((prev) => prev.filter((item) => item._id !== deletedId));
      if (selectedEnquiry && selectedEnquiry._id === deletedId) {
        setSelectedEnquiry(null);
      }
    }
    fetchEnquiries();
  };

  return (
    <AdminLayout>
      <div className="ce-page">
        {/* Subtle background decorative floating shapes */}
        <div className="ce-bg-shapes" aria-hidden="true">
          <div className="ce-bg-shape ce-bg-shape-1" />
          <div className="ce-bg-shape ce-bg-shape-2" />
          <div className="ce-bg-shape ce-bg-shape-3" />
        </div>

        {/* Header */}
        <div className="ce-header">
          <div className="ce-header-left">
            <div className="ce-icon-badge">
              <Mail size={22} />
            </div>
            <div>
              <h1 className="ce-title">Contact Enquiries</h1>
              <p className="ce-subtitle">Manage customer contact messages and enquiries</p>
            </div>
          </div>
        </div>

        {/* Stack Cards */}
        <StatCards cards={statCards} />

        {/* Filter row: Search + Date Filter + All Status Dropdown */}
        <div className="ce-filter-row">
          <div className="ce-search-wrap">
            <Search size={16} className="ce-search-icon" />
            <input
              type="text"
              className="ce-search-input"
              placeholder="Search by name, phone, email, subject..."
              value={searchTerm}
              onChange={(ev) => setSearchTerm(ev.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="ce-search-clear-btn"
                onClick={() => setSearchTerm("")}
                title="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>

          <div className="ce-filter-right-controls">
            {/* Date Filter Box (Left of All Statuses Dropdown) */}
            <button
              type="button"
              className={"ce-date-filter-btn" + (filterStart || filterEnd ? " is-active" : "")}
              onClick={() => setShowDateFilter(true)}
              title="Filter by submitted date"
            >
              <Calendar size={15} />
              <span>{dateFilterButtonLabel}</span>
              {filterStart || filterEnd ? (
                <span
                  className="ce-date-clear-pill"
                  title="Clear date filter"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearDate();
                  }}
                >
                  ×
                </span>
              ) : (
                <ChevronDown size={14} />
              )}
            </button>

            {/* All Statuses Dropdown */}
            <div className="ce-filter-select-wrap">
              <CustomSelect
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                options={STATUS_FILTER_OPTIONS}
              />
            </div>
          </div>
        </div>

        {/* Loading / Error / Table Card */}
        {loading ? (
          <div className="ce-loading">
            <div className="ce-spinner" />
            <p>Loading enquiries...</p>
          </div>
        ) : error ? (
          <div className="ce-error">
            <p>{error}</p>
          </div>
        ) : (
          <div className="ce-card">
            <div className="ce-table-wrap">
              <table className="ce-table">
                <thead>
                  <tr>
                    <th className="ce-col-sno">S.NO</th>
                    <th className="ce-col-name">Name</th>
                    <th className="ce-col-phone">Phone</th>
                    <th className="ce-col-email">Email</th>
                    <th className="ce-col-subject">Subject</th>
                    <th className="ce-col-status">Status</th>
                    <th className="ce-col-date">Submitted Date</th>
                    <th className="ce-col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEnquiries.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="ce-empty-cell">
                        <div className="ce-empty-content">
                          <MessageSquare size={34} className="ce-empty-icon" />
                          <div className="ce-empty-title">No contact enquiries found</div>
                          <div className="ce-empty-sub">
                            {searchTerm || statusFilter !== "All" || filterStart || filterEnd
                              ? "Try adjusting your search query, status filter, or date range."
                              : "Customer messages submitted from the website will appear here."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((item, index) => {
                      const sno = startIndex + index + 1;
                      return (
                        <tr key={item._id}>
                          <td className="ce-td-sno">{sno}</td>
                          <td className="ce-td-name">{item.name || "-"}</td>
                          <td className="ce-td-phone">{item.phone || "-"}</td>
                          <td className="ce-td-email" title={item.email}>{item.email || "-"}</td>
                          <td className="ce-td-subject" title={item.subject}>{item.subject || "-"}</td>
                          <td className="ce-td-status"><StatusBadge status={item.status} /></td>
                          <td className="ce-td-date">{formatDate(item.createdAt)}</td>
                          <td className="ce-td-actions">
                            <div className="ce-action-group">
                              <button
                                type="button"
                                className="ce-action-btn ce-action-btn--view"
                                title="View details"
                                onClick={() => setSelectedEnquiry(item)}
                              >
                                <Eye size={15} />
                              </button>
                              <button
                                type="button"
                                className="ce-action-btn ce-action-btn--delete"
                                title="Delete enquiry"
                                onClick={() => setDeleteTarget(item)}
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination below table */}
            {filteredEnquiries.length > 0 && (
              <div className="ce-pagination">
                <div className="ce-pagination-left">
                  <span className="ce-pagination-info">
                    Showing <strong>{filteredEnquiries.length === 0 ? 0 : startIndex + 1}–{endIndex}</strong> of <strong>{filteredEnquiries.length}</strong> enquiries
                  </span>
                  <div className="ce-per-page-select">
                    <span>Rows per page:</span>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      {PER_PAGE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="ce-pagination-controls">
                  <button
                    type="button"
                    className="ce-page-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={safeCurrentPage <= 1}
                    title="First page"
                  >
                    «
                  </button>
                  <button
                    type="button"
                    className="ce-page-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={safeCurrentPage <= 1}
                    title="Previous page"
                  >
                    ‹
                  </button>

                  {getPageNumbers().map((pageNum) => (
                    <button
                      key={pageNum}
                      type="button"
                      className={"ce-page-btn" + (pageNum === safeCurrentPage ? " is-active" : "")}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  ))}

                  <button
                    type="button"
                    className="ce-page-btn"
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safeCurrentPage >= totalPages}
                    title="Next page"
                  >
                    ›
                  </button>
                  <button
                    type="button"
                    className="ce-page-btn"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={safeCurrentPage >= totalPages}
                    title="Last page"
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Date Filter Popup (Dashboard design & logic) */}
        {showDateFilter && (
          <DateFilterPopup
            initialStart={filterStart}
            initialEnd={filterEnd}
            initialPreset={filterPreset}
            onApply={handleDateApply}
            onClose={() => setShowDateFilter(false)}
            hasRecords={dateFilteredEnquiries.length > 0}
          />
        )}

        {/* View Details Modal */}
        <EnquiryDetailsModal
          enquiry={selectedEnquiry}
          onClose={() => setSelectedEnquiry(null)}
          onStatusUpdate={handleStatusUpdate}
          isUpdating={updatingId !== null}
          onDelete={(item) => setDeleteTarget(item)}
        />

        {/* Delete Confirmation Modal */}
        {deleteTarget && (
          <DeleteConfirmModal
            isOpen={true}
            title="Delete Contact Enquiry"
            message="Are you sure you want to delete this enquiry?"
            itemName={deleteTarget.name || deleteTarget.email}
            isDeleting={isDeleting}
            onCancel={() => !isDeleting && setDeleteTarget(null)}
            onConfirm={() => handleDeleteEnquiry(deleteTarget._id)}
          />
        )}

        {/* Delete Success Modal */}
        {deleteSuccess && (
          <DeleteSuccessModal
            isOpen={true}
            message="Deleted Successfully"
            subtitle="The contact enquiry has been deleted successfully."
            onClose={handleDeleteSuccessClose}
          />
        )}

        {/* Status update success modal */}
        {showUpdateSuccess && (
          <UpdateSuccessModal
            isOpen={true}
            message="Updated successfully!"
            onClose={() => setShowUpdateSuccess(false)}
          />
        )}

        {/* Toast notification */}
        {toast && (
          <Toast
            key={Date.now()}
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
}